/**
 * The guided run a trialist walks once their dashboard is built.
 *
 * Every trigger resolves to instrumentation that already exists — the
 * onFeatureUsage observer, the widgetFilters diff. The rail observes the
 * product; nothing inside a widget reports to the rail. Same rule the demo
 * guide follows, and for the same reason: the alternative is threading "tell
 * the guide I happened" callbacks into every surface the guide might point at.
 *
 * Import-free apart from types, so the step table is testable under node.
 */

import type { RailStepId } from "../types";
import type { StepTrigger } from "./triggers";

/**
 * A widget to ring, identified by REGISTRY BASE ID rather than grid id.
 *
 * The demo guide can name grid ids literally (applyDemoTemplate writes them
 * verbatim), but a subscriber's saved tab holds composite ids minted per
 * instance — "NewsquawkHeadlines-a8f3k2". There is no name to look up, so the
 * only stable handle is the widget type. Resolved at runtime in railTargets.ts.
 */
export type RailTargetSpec = {
  baseId: string;
  /**
   * With two Headlines instances on a tab, prefer the one still carrying
   * everything. Starter templates narrow the secondary with a category filter,
   * and "apply it to your feed" means the feed, not the sub-feed.
   */
  prefer?: "unfiltered";
};

export type RailStep = {
  id: RailStepId;
  /** Imperative — what to do, not what the feature is called. */
  instruction: string;
  hint?: string;
  /** A widget to ring, via WidgetSpotlightContext. */
  target?: RailTargetSpec;
  /** A control inside the target widget to light up alongside the ring. */
  control?: "highlight" | "filter" | "popout";
  /**
   * App chrome to mark instead of a widget.
   *
   * WidgetSpotlightContext cannot reach this: it is keyed on a grid id and
   * consumed by WidgetContainer, and the squawk button is neither. Handled by
   * useSquawkPulse, which reads the store directly — see that file for why a
   * context provider physically could not work here.
   */
  chrome?: "squawk";
  /**
   * Needs live product access to be completable at all.
   *
   * True for the two steps that reach the desk: in demo mode the squawk greys
   * out every channel, and Tawk serves the sales property rather than the
   * analyst one. Dropping them beats asking for something the product refuses.
   */
  requiresLiveAccess?: boolean;
  /** A destination the rail can take them to itself. Navigation only. */
  action?: { to: string; label: string };
  trigger: StepTrigger;
};

const HEADLINES: RailTargetSpec = {
  baseId: "NewsquawkHeadlines",
  prefer: "unfiltered",
};

/**
 * Five beats, in the order they earn their keep:
 *
 * 1. squawk    — the signature product and the cheapest action on the list. It
 *                goes first because it is the one thing no competitor has, and
 *                because it keeps paying off in the background while they do
 *                everything else.
 * 2. chat      — the other half of the same idea. The squawk is the desk
 *                talking to them; this is them talking back. Placed second
 *                rather than last because it is the highest-value touch point
 *                on the list for a trialist deciding whether to stay, and a
 *                step at the end of a run is a step many people never reach.
 * 3. watchlist — build one, in the builder.
 * 4. highlight — apply the thing they just built. Cause and effect: this is
 *                why the builder comes first.
 * 5. popout    — the second-monitor moment.
 *
 * Steps 1 and 2 are the two that can be dropped — see visibleRailSteps.
 */
export const RAIL_STEPS: RailStep[] = [
  {
    id: "squawk",
    instruction: "Turn on the squawk",
    hint: "The Squawk button is marked. Pick a channel and our analysts read the tape to you live — it keeps running while you do everything else.",
    chrome: "squawk",
    requiresLiveAccess: true,
    /**
     * No action button, deliberately.
     *
     * The rail could call handleChannelSelect itself — a click here is a real
     * user gesture, so autoplay policy would be satisfied — but that bypasses
     * the demo-mode and per-channel role guards SquawkPlayerButton applies, and
     * it picks a channel on their behalf. Marking the real control and letting
     * them choose is the smaller promise.
     */
    trigger: {
      kind: "featureUsage",
      featureName: "squawk_player",
      featureAction: "channel_select",
    },
  },
  {
    id: "chat",
    instruction: "Ask an analyst something",
    hint: "The chat bubble in the bottom-right corner goes to the desk itself, not a support queue — the same analysts writing the headlines. Ask what they are watching today.",
    requiresLiveAccess: true,
    /**
     * The only step whose signal did not already exist.
     *
     * Every other trigger reuses instrumentation the product emits for GA4, but
     * Tawk was never tracked at all — so TawkWrapper now reports chat open and
     * first message, which this consumes. Completing on `opened` rather than
     * `message_sent`: if the desk is quiet, someone who opened the chat and
     * found nobody there has still done what was asked, and stranding them on a
     * step that depends on an analyst being online would be unfair.
     */
    trigger: {
      kind: "featureUsage",
      featureName: "analyst_chat",
      featureAction: "opened",
    },
  },
  {
    id: "watchlist",
    instruction: "Build a watchlist",
    hint: "The terms you care about — tickers, keywords, or a plain-English description the builder turns into both.",
    action: { to: "/watchlists-filters?create=ticker", label: "Open Watchlists" },
    trigger: {
      kind: "featureUsage",
      featureName: "watchlist",
      featureAction: "create",
    },
  },
  {
    id: "highlight",
    instruction: "Apply it to your feed",
    hint: "The marked highlighter in the headlines panel header opens the highlight menu. Pick your watchlist there — highlighting marks matching headlines in place, and the funnel beside it hides everything else instead.",
    target: HEADLINES,
    control: "highlight",
    trigger: { kind: "widgetFilters" },
  },
  {
    id: "popout",
    instruction: "Put a panel on its own screen",
    hint: "The marked icon opens this panel as its own window, which can live on a second monitor. It stays connected to the same feed.",
    target: HEADLINES,
    control: "popout",
    trigger: {
      kind: "featureUsage",
      featureName: "widget",
      featureAction: "popout",
    },
  },
];

export const RAIL_COMPLETION = {
  headline: "That's your dashboard set up.",
  body: "Your watchlist is marking the feed and you have a panel on its own screen. The squawk and the analyst chat stay where they are — anything left is in the Getting started list, bottom left.",
};
